import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck } from "lucide-react";

const PERMISSIONS = [
  { key: "earn", label: "Add reward points", hint: "POST /rewards/earn" },
  { key: "redeem", label: "Redeem reward points", hint: "POST /rewards/redeem" },
  { key: "view_customers", label: "View customer list & search", hint: "GET /customers" },
  { key: "create_customer", label: "Create new members", hint: "POST /customers" },
  { key: "manage_offers", label: "Manage offers", hint: "POST /offers" },
  { key: "view_ledger", label: "View transaction ledger", hint: "GET /transactions" },
  { key: "view_reports", label: "View reports & analytics", hint: "GET /admin/reports" },
  { key: "approve_rewards", label: "Approve / reject staff submissions",
    hint: "Managers only. POST /transactions/{id}/approve|reject" },
];

export default function StaffPermissionsDialog({ user, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const initial = user.permissions || PERMISSIONS.reduce((a, p) => ({ ...a, [p.key]: true }), {});
  const [perms, setPerms] = useState(initial);

  React.useEffect(() => {
    if (open) setPerms(user.permissions || initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/staff/${user.id}/permissions`, { permissions: perms });
      toast.success(`Permissions updated for ${user.username}`);
      onSaved?.(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            data-testid={`perms-btn-${user.username}`}
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Permissions
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid={`perms-dialog-${user.username}`}>
        <DialogHeader>
          <DialogTitle>
            Permissions · <span className="font-mono">{user.username}</span>
          </DialogTitle>
        </DialogHeader>
        {user.super_admin ? (
          <div className="py-3 text-sm" style={{ color: "var(--text-muted)" }}>
            This is the super admin. Toggles here are saved but have no
            effect while the account remains super admin — they bypass every
            permission check.
          </div>
        ) : user.role === "admin" ? (
          <div className="py-3 text-sm" style={{ color: "var(--text-muted)" }}>
            Manager (regular admin) — permissions below are enforced exactly
            like they are for staff.
          </div>
        ) : null}
        <div className="space-y-3 py-2">
          {PERMISSIONS.map((p) => (
            <div
              key={p.key}
              className="flex items-start justify-between gap-4 py-2 border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{p.label}</div>
                <div className="label-caps mt-0.5">{p.hint}</div>
              </div>
              <Switch
                data-testid={`perm-${p.key}-${user.username}`}
                checked={!!perms[p.key]}
                onCheckedChange={(v) => setPerms((prev) => ({ ...prev, [p.key]: !!v }))}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={saving}
            data-testid={`perms-save-${user.username}`}
            className="rounded-full text-white"
            style={{ background: "var(--navy)" }}
          >
            {saving ? "Saving…" : "Save permissions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
